import { hasAiAccess } from '../../utils/aiAccess';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Loader2, Mail, Plus, Trash2, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import SettingsShell from './SettingsShell';
import TeamAgents from '../../components/TeamAgents';
import MemberPermissions from './MemberPermissions';
import { workspaceAPI } from '../../services/api';

const TeamSettings = () => {
  const { user } = useAuth();
  const teamInboxEnabled = Boolean(user?.teamInboxEnabled);
  const isOwner = user?.role === 'owner';

  const [teamMembers, setTeamMembers] = useState([]);
  const [editingPermissions, setEditingPermissions] = useState(null);
  const [permissionNotice, setPermissionNotice] = useState('');
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [pendingInvites, setPendingInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesError, setInvitesError] = useState('');

  useEffect(() => {
    let active = true;
    const loadMembers = async () => {
      if (!teamInboxEnabled) return;
      setTeamLoading(true);
      setTeamError('');
      try {
        const { data } = await workspaceAPI.getMembers();
        if (active) setTeamMembers(Array.isArray(data) ? data : []);
      } catch (err) {
        if (active) {
          const msg = err?.response?.data?.error || err?.message || 'Failed to load team';
          setTeamError(typeof msg === 'string' ? msg : 'Failed to load team');
        }
      } finally {
        if (active) setTeamLoading(false);
      }
    };
    loadMembers();
    return () => {
      active = false;
    };
  }, [teamInboxEnabled]);

  useEffect(() => {
    let active = true;
    const loadInvites = async () => {
      if (!teamInboxEnabled || !isOwner) return;
      setInvitesLoading(true);
      setInvitesError('');
      try {
        const { data } = await workspaceAPI.getInvites();
        if (active) setPendingInvites(Array.isArray(data) ? data : []);
      } catch (err) {
        if (active) {
          const status = err?.response?.status;
          if (status === 404) {
            setPendingInvites([]);
            setInvitesError('');
          } else {
            const msg = err?.response?.data?.error || err?.message || 'Failed to load invites';
            setInvitesError(typeof msg === 'string' ? msg : 'Failed to load invites');
          }
        }
      } finally {
        if (active) setInvitesLoading(false);
      }
    };
    loadInvites();
    return () => {
      active = false;
    };
  }, [teamInboxEnabled, isOwner]);

  const handleInvite = async event => {
    event.preventDefault();
    if (!event.currentTarget.reportValidity()) return;
    if (inviting || !inviteEmail.trim()) return;
    setInviting(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      const emailToInvite = inviteEmail.trim();
      await workspaceAPI.invite(emailToInvite);
      setInviteEmail('');
      setInviteSuccess(`Invitation sent to ${emailToInvite}`);
      if (isOwner) {
        try {
          const inviteRes = await workspaceAPI.getInvites();
          setPendingInvites(Array.isArray(inviteRes?.data) ? inviteRes.data : []);
        } catch (err) {
          if (err?.response?.status !== 404) {
            const msg = err?.response?.data?.error || err?.message || 'Failed to load invites';
            setInvitesError(typeof msg === 'string' ? msg : 'Failed to load invites');
          }
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to invite member';
      setInviteError(typeof msg === 'string' ? msg : 'Failed to invite member');
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvite = async (id) => {
    setInvitesError('');
    try {
      await workspaceAPI.cancelInvite(id);
      setPendingInvites(current => current.filter(invite => invite.id !== id));
    } catch (err) { setInvitesError('Could not cancel invitation. Please try again.'); }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member from the workspace?')) return;
    setTeamError('');
    try {
      await workspaceAPI.removeMember(memberId);
      const { data } = await workspaceAPI.getMembers();
      setTeamMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to remove member';
      setTeamError(typeof msg === 'string' ? msg : 'Failed to remove member');
    }
  };

  if (!teamInboxEnabled) {
    return (
      <SettingsShell
        title="Team & access"
        description={hasAiAccess(user) ? "Manage the people and AI agents on your team." : "Manage the people on your team."}
      >
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
          Team collaboration is not enabled yet.
        </div>
        {hasAiAccess(user) && <TeamAgents />}
      </SettingsShell>
    );
  }

  return (
    <SettingsShell
      title="Team & access"
      description="Manage your team’s access and responsibility for enquiries and conversations."
    >
      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
        <Link to="/settings/workspace" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-primary-700 underline">Workspace overview</Link>
        <h3 className="break-words font-semibold text-gray-900">{user?.workspaceCompanyName || (isOwner && user?.businessName) || 'Your workspace'}</h3>
        <p className="text-sm text-gray-600">{isOwner ? 'You are the owner. Invite people below, then manage their permissions once they join.' : 'You are a member. Ask the owner listed below to invite someone or change your permissions.'}</p>
      </div>
      {isOwner && (
        <form id="team-invite" onSubmit={handleInvite} className="rounded-xl border border-primary-200 bg-primary-50 p-4 space-y-4">
          <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 mb-1">
            Invite someone to this workspace
          </label>
          <p className="text-sm leading-6 text-gray-600">They’ll receive a link to join using this email address. They can sign in or create an account from the invitation; it expires after seven days.</p>
          <p className="text-sm text-gray-600">Each account can belong to one workspace at a time. Someone already in an active workspace cannot join another.</p>
          <div className="flex flex-col gap-2">
            <div className="relative group">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
              <input
                id="invite-email"
                type="email"
                required
                disabled={inviting}
                autoComplete="email"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder="name@business.co.zw"
                className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-gray-900 font-medium transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={inviting || !inviteEmail.trim()}
              className="w-full bg-primary-600 text-white p-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary-700 active:bg-primary-800 transition-all disabled:opacity-50"
            >
              {inviting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Send invitation
                </>
              )}
            </button>
          </div>
          {inviteError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{inviteError}</p>}
          {inviteSuccess && (
            <div role="status" className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-xs font-semibold flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {inviteSuccess}
            </div>
          )}
        </form>
      )}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 space-y-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 rounded-lg text-primary-600">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-gray-900 leading-tight">
              Active Members
            </h3>
          </div>
          <span className="bg-gray-100 text-gray-600 text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">
            {isOwner ? 'Owner' : 'Member'}
          </span>
        </div>

        {permissionNotice && <p role="status" className="text-sm text-green-800">{permissionNotice}</p>}
        {teamError && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs font-medium">
            {teamError}
          </div>
        )}

        {teamLoading ? (
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-400 font-medium">
            <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
            Syncing team...
          </div>
        ) : (
          <div className="space-y-2">
            {teamMembers.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                <p className="text-xs text-gray-500 font-medium">No team members yet.</p>
              </div>
            ) : (
              teamMembers.map((member) => (
                <div key={member.userId} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-700">{(member.fullName || member.email || '?').charAt(0).toUpperCase()}</div>
                    <div className="min-w-0 flex-1"><p className="break-words text-sm font-semibold text-gray-900">{member.fullName || 'Workspace member'}</p><p className="break-all text-xs text-gray-600">{member.email}</p></div>
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">{member.role === 'owner' ? 'Owner' : 'Member'}</span>
                  </div>
                  {member.role === 'owner' && <p className="mt-3 text-xs text-gray-500">Full workspace access</p>}
                  {isOwner && member.role !== 'owner' && <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" aria-label={`Manage permissions for ${member.fullName || member.email}`} aria-expanded={editingPermissions === member.userId} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-primary-700" onClick={() => { setEditingPermissions(member.userId); setPermissionNotice(''); }}>Permissions</button>
                    <button type="button" aria-label={`Remove ${member.fullName || member.email}`} onClick={() => handleRemoveMember(member.userId)} className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-red-50 hover:text-red-700"><Trash2 className="h-4 w-4" />Remove</button>
                  </div>}
                  {isOwner && member.role !== 'owner' && editingPermissions === member.userId && <MemberPermissions key={member.userId} member={member}
                    onCancel={() => setEditingPermissions(null)}
                    onSaved={permissions => { setTeamMembers(current => current.map(row => row.userId === member.userId ? { ...row, permissions } : row)); setEditingPermissions(null); setPermissionNotice(`Permissions updated for ${member.fullName || member.email}.`); }} />}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {isOwner && (
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-900 uppercase tracking-tight text-xs">Pending invitations</h3>
            {invitesLoading && (
              <Loader2 className="w-3 h-3 animate-spin text-primary-500" />
            )}
          </div>
          {invitesError && (
            <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs font-medium">
              {invitesError}
            </div>
          )}
          {pendingInvites.length === 0 && !invitesLoading && (
            <p className="text-xs text-gray-400 font-medium italic">No pending invitations.</p>
          )}
          {pendingInvites.length > 0 && (
            <div className="grid gap-2">
              {pendingInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50/30 px-3 py-3"
                >
                  <div className="min-w-0 flex-1 basis-40">
                    <div className="break-all text-sm font-semibold text-gray-700">
                      {invite.email}
                    </div>
                    <div className="text-xs text-gray-400 font-medium lowercase">
                      expires {new Date(invite.expiresAt || invite.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                  <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                    {new Date(invite.expiresAt || invite.expires_at).getTime() <= Date.now() ? 'EXPIRED' : 'INVITED'}
                  </span>
                  <button type="button" className="min-h-[44px] text-red-700 text-sm" onClick={() => handleCancelInvite(invite.id)}>Cancel invitation</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {hasAiAccess(user) && <TeamAgents />}
    </SettingsShell>
  );
};

export default TeamSettings;
